import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocalization } from '../../../Hooks/useLocalization'
import {
  setIsCertificateInstancesRequested,
  setIsCertificatesRequested,
  setIsCompaniesRequested,
} from '../../../Store/DataSlice'
import { type RootState } from '../../../Store/Store'
import { NaceCodes } from '../../../Utils/NaceCodes'
import './ObsoleteCertificateNaceCodes.scss'
import { toBelgianDate } from '../../../Utils/Formatting'
import { Button } from '../../../Components/Button/Button'
import {
  exportCsv,
  exportExcel,
} from '../../../Components/TableList/TableListExport'
import { type configuration } from '../../../Components/TableList/TableList'

const validNaceCodes = NaceCodes.map((nc) => nc.code)

export function ObsoleteCertificateNaceCodes() {
  const dispatch = useDispatch()
  const localization = useLocalization('reporting')
  const componentsLocalization = useLocalization('components')

  // certificate instances
  const isCertificateInstancesRequested = useSelector(
    (state: RootState) => state.data.isCertificateInstancesRequested
  )
  useEffect(() => {
    if (!isCertificateInstancesRequested)
      dispatch(setIsCertificateInstancesRequested(true))
  }, [isCertificateInstancesRequested, dispatch])

  const allCertificateInstances = useSelector(
    (state: RootState) => state.data.certificateInstances
  )

  // certificates
  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])

  const allCertificates = useSelector(
    (state: RootState) => state.data.certificates
  )

  // companies
  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])
  const allCompanies = useSelector((state: RootState) => state.data.companies)

  const obsoleteCertificatesData = useMemo(() => {
    const relevantCertificates = allCertificates.filter((cert) =>
      cert.naceCodes.some((nc) => !validNaceCodes.includes(nc))
    )

    const data: ObsoleteCertificateData[] = []
    for (const relevantCertificate of relevantCertificates) {
      const certificateInstance = allCertificateInstances.find(
        (certInst) =>
          certInst.vatNumber ===
          relevantCertificate.certificateInstanceVatNumber
      )
      const company = allCompanies.find((company) =>
        relevantCertificate.companyBranches?.some(
          (cb) => cb.vatNumber === company.vatNumber
        )
      )
      data.push({
        certificateLink: `https://besacc-site.s3.eu-west-1.amazonaws.com/index.html#certificate-detail/${relevantCertificate.uuid}`,
        certificateInstanceLink: `https://besacc-site.s3.eu-west-1.amazonaws.com/index.html#certificate-instance-detail/${certificateInstance?.vatNumber}`,
        companyLink: `https://besacc-site.s3.eu-west-1.amazonaws.com/index.html#company-detail/${company?.vatNumber}`,
        invalidNaceCodes: relevantCertificate.naceCodes
          .filter((nc) => !validNaceCodes.includes(nc))
          .join(','),
        // certificate
        certificateUuid: relevantCertificate.uuid,
        certificateNumber: relevantCertificate.certificateNumber,
        scope: relevantCertificate.scope,
        naceCodes: relevantCertificate.naceCodes.join(','),
        status: relevantCertificate.status,
        type: relevantCertificate.type,
        validFrom: toBelgianDate(relevantCertificate.validFrom),
        validUntil: toBelgianDate(relevantCertificate.validUntil),
        extendedUntil: toBelgianDate(relevantCertificate.extendedUntil),
        issueDate: toBelgianDate(relevantCertificate.issueDate),
        isGroupCertificate: relevantCertificate.isGroupCertificate
          ? 'true'
          : 'false',
        deniedPeriods:
          relevantCertificate.deniedPeriods
            ?.map(
              (deniedPeriod) =>
                `from: ${toBelgianDate(
                  deniedPeriod.deniedFrom
                )} until: ${toBelgianDate(deniedPeriod.deniedUntil)} status: ${
                  deniedPeriod.deniedStatus
                }`
            )
            .join(', ') ?? '',
        suspendedFrom: toBelgianDate(relevantCertificate.suspendedFrom),
        withdrawnFrom: toBelgianDate(relevantCertificate.withdrawnFrom),
        // certificate instance
        certInstVatNumber: certificateInstance?.vatNumber ?? '',
        certInstName: certificateInstance?.name ?? '',
        certInstEmail: certificateInstance?.email ?? '',
        certInstWebsite: certificateInstance?.website ?? '',
        certInstTelephone: certificateInstance?.telephone ?? '',
        certInstAddressLine1: certificateInstance?.addressLine1 ?? '',
        certInstPostalCode: certificateInstance?.postalCode ?? '',
        certInstCity: certificateInstance?.city ?? '',
        certInstCountryCode: certificateInstance?.countryCode ?? '',
        certInstCoordinators:
          certificateInstance?.coordinators
            ?.map(
              (coordinator) =>
                `${coordinator.name} ${coordinator.email} ${coordinator.telephone}`
            )
            .join(',') ?? '',
        // company
        companyVatNumber: company?.vatNumber ?? '',
        companyName: company?.name ?? '',
        companyLegalForm: company?.legalForm ?? '',
        companyEmail: company?.email ?? '',
        companyWebsite: company?.website ?? '',
        companyAddressLine1: company?.addressLine1 ?? '',
        companyPostalCode: company?.postalCode ?? '',
        companyCity: company?.city ?? '',
        companyCountryCode: company?.countryCode ?? '',
      })
    }
    return data
  }, [allCertificates, allCertificateInstances, allCompanies])

  return (
    <div className="obsolete-certificate-nace-codes">
      {allCertificateInstances.length === 0 ||
      allCertificates.length === 0 ||
      allCompanies.length === 0 ? (
        <div className="loading">
          {localization.text(
            'data-is-loading',
            'De data word momenteel ingeladen.'
          )}
        </div>
      ) : (
        <>
          <h1>
            {localization.text(
              'obsolete-certificate-nace-codes-title',
              'Certificaten met vervallen nace codes'
            )}
          </h1>
          <div className="actions-row">
            <div>
              {localization.text(
                'obsolete-certificate-nace-codes-count',
                '{count} certificaten gevonden',
                { count: `${obsoleteCertificatesData.length}` }
              )}
            </div>
            <Button
              importance={'primary'}
              size="small"
              text={componentsLocalization.text('export-csv', 'Export csv')}
              onClick={() => {
                exportCsv(
                  'obsolete-certificate-nace-codes',
                  obsoleteCertificatesData,
                  exportConfigurations,
                  (t) => t.certificateUuid
                )
              }}
            />
            <Button
              importance={'primary'}
              size="small"
              text={componentsLocalization.text('export-excel', 'Export excel')}
              onClick={() => {
                exportExcel(
                  'obsolete-certificate-nace-codes',
                  obsoleteCertificatesData,
                  exportConfigurations,
                  (t) => t.certificateUuid
                )
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface ObsoleteCertificateData {
  certificateLink: string
  certificateInstanceLink: string
  companyLink: string
  invalidNaceCodes: string
  // certificate
  certificateUuid: string // primary key
  certificateNumber: string
  scope: string
  naceCodes: string
  status: string
  type: string // CertificateType.name
  validFrom: string
  validUntil: string
  extendedUntil: string
  issueDate: string
  isGroupCertificate: string
  deniedPeriods: string
  suspendedFrom: string
  withdrawnFrom: string
  // certificate instance
  certInstVatNumber: string // primary key
  certInstName: string
  certInstEmail: string
  certInstWebsite: string
  certInstTelephone: string
  certInstAddressLine1: string
  certInstPostalCode: string
  certInstCity: string
  certInstCountryCode: string
  certInstCoordinators: string
  // company
  companyVatNumber: string // primary key
  companyName: string
  companyLegalForm: string
  companyEmail: string
  companyWebsite: string
  companyAddressLine1: string
  companyPostalCode: string
  companyCity: string
  companyCountryCode: string
}

const exportConfigurations: {
  [property in keyof ObsoleteCertificateData]: configuration
} = {
  certificateLink: { show: true, label: 'certificateLink' },
  certificateInstanceLink: { show: true, label: 'certificateInstanceLink' },
  companyLink: { show: true, label: 'companyLink' },
  invalidNaceCodes: { show: true, label: 'invalidNaceCodes' },
  // certificate
  certificateUuid: { show: true, label: 'certificateUuid' },
  certificateNumber: { show: true, label: 'certificateNumber' },
  scope: { show: true, label: 'scope' },
  naceCodes: { show: true, label: 'naceCodes' },
  status: { show: true, label: 'status' },
  type: { show: true, label: 'type' }, // CertificateType.name
  validFrom: { show: true, label: 'validFrom' },
  validUntil: { show: true, label: 'validUntil' },
  extendedUntil: { show: true, label: 'extendedUntil' },
  issueDate: { show: true, label: 'issueDate' },
  isGroupCertificate: { show: true, label: 'isGroupCertificate' },
  deniedPeriods: { show: true, label: 'deniedPeriods' },
  suspendedFrom: { show: true, label: 'suspendedFrom' },
  withdrawnFrom: { show: true, label: 'withdrawnFrom' },
  // certificate instance
  certInstVatNumber: { show: true, label: 'certInstVatNumber' },
  certInstName: { show: true, label: 'certInstName' },
  certInstEmail: { show: true, label: 'certInstEmail' },
  certInstWebsite: { show: true, label: 'certInstWebsite' },
  certInstTelephone: { show: true, label: 'certInstTelephone' },
  certInstAddressLine1: { show: true, label: 'certInstAddressLine1' },
  certInstPostalCode: { show: true, label: 'certInstPostalCode' },
  certInstCity: { show: true, label: 'certInstCity' },
  certInstCountryCode: { show: true, label: 'certInstCountryCode' },
  certInstCoordinators: { show: true, label: 'certInstCoordinators' },
  // company
  companyVatNumber: { show: true, label: 'companyVatNumber' },
  companyName: { show: true, label: 'companyName' },
  companyLegalForm: { show: true, label: 'companyLegalForm' },
  companyEmail: { show: true, label: 'companyEmail' },
  companyWebsite: { show: true, label: 'companyWebsite' },
  companyAddressLine1: { show: true, label: 'companyAddressLine1' },
  companyPostalCode: { show: true, label: 'companyPostalCode' },
  companyCity: { show: true, label: 'companyCity' },
  companyCountryCode: { show: true, label: 'companyCountryCode' },
}
